import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCalculatePermitFees } from '@/hooks/api/usePermits';
import { useBillingBalance } from '@/hooks/api/useBilling';
import PermitSearchForm from '@/components/permits/PermitSearchForm';
import FeeResultsDisplay from '@/components/permits/FeeResultsDisplay';
import { ErrorMessage, LoadingSpinner } from '@/components/ui';
import type { PermitCalculationRequest, PermitCalculationResponse } from '@/types/api.types';

export default function PermitSearchPage() {
  const [searchResult, setSearchResult] = useState<PermitCalculationResponse | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  const calculateFees = useCalculatePermitFees();
  const { data: balanceData, refetch: refetchBalance } = useBillingBalance();

  // Scroll to results when they appear
  useEffect(() => {
    if (searchResult && resultsRef.current && resultsRef.current.scrollIntoView) {
      resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [searchResult]);

  const handleSearch = async (data: PermitCalculationRequest) => {
    setSearchError(null);
    setSearchResult(null);

    try {
      const result = await calculateFees.mutateAsync(data);
      setSearchResult(result);
      // Refetch balance after successful search (user was charged)
      refetchBalance();
    } catch (error: unknown) {
      const err = error as { response?: { status?: number; data?: { detail?: string | { message?: string } } } };
      // Handle insufficient balance error (402)
      if (err.response?.status === 402) {
        const detail = err.response.data?.detail;
        if (typeof detail === 'object' && detail.message) {
          setSearchError(detail.message);
        } else {
          setSearchError('Insufficient account balance. Please add funds to continue.');
        }
      } else if (err.response?.status === 422) {
        setSearchError(
          (typeof err.response.data?.detail === 'string' ? err.response.data.detail : null) || 'Fee calculation failed. Please try again.'
        );
      } else {
        setSearchError((typeof err.response?.data?.detail === 'string' ? err.response.data.detail : null) || 'An error occurred during calculation.');
      }
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Permit Fee Calculator</h1>
          <p className="text-gray-600">
            Calculate electrical permit fees for your project with AI-powered municipality data
            extraction
          </p>
        </div>

        {/* Balance Display */}
        {balanceData && (
          <div className="mb-6 bg-white rounded-lg shadow-sm p-4 border border-gray-200">
            <div className="flex justify-between items-center">
              <div>
                <span className="text-sm text-gray-600">Credit Balance:</span>
                <span className="ml-2 text-lg font-semibold text-gray-900">
                  {Number(balanceData.credit_balance ?? balanceData.current_balance).toFixed(1)} credits
                </span>
              </div>
              <div className="text-xs text-gray-500">
                Searches cost 1 credit each
              </div>
            </div>
            {Number(balanceData.credit_balance ?? balanceData.current_balance) < 1 && (
              <div className="mt-2 text-sm text-amber-700 bg-amber-50 p-2 rounded">
                Low credits. Please <Link to="/billing" className="underline hover:text-amber-900">purchase credits</Link> to continue searching.
              </div>
            )}
          </div>
        )}

        {/* Search Form */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Project Information
          </h2>
          <PermitSearchForm onSubmit={handleSearch} isLoading={calculateFees.isPending} />
        </div>

        {/* Loading State */}
        {calculateFees.isPending && (
          <div className="bg-white rounded-lg shadow-md p-8">
            <LoadingSpinner size="lg" />
            <p className="text-center text-gray-600 mt-4">
              Analyzing municipality data and calculating permit fees...
            </p>
            <p className="text-center text-sm text-gray-500 mt-2">
              This may take up to 2 minutes depending on the municipality
            </p>
          </div>
        )}

        {/* Error State */}
        {searchError && (
          <div className="mb-6">
            <ErrorMessage message={searchError} />
          </div>
        )}

        {/* Results */}
        {searchResult && !calculateFees.isPending && (
          <div ref={resultsRef} className="mb-6 scroll-mt-20">
            <FeeResultsDisplay result={searchResult} />
          </div>
        )}

        {/* Info Box */}
        {!searchResult && !calculateFees.isPending && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <span className="text-xl">ℹ️</span>
              <div>
                <h3 className="font-semibold text-blue-900 mb-1">How it works</h3>
                <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                  <li>Select your project location (city and state)</li>
                  <li>Choose your project type and provide details</li>
                  <li>Our AI analyzes the municipality's permit requirements</li>
                  <li>Get accurate fee calculations with detailed breakdowns</li>
                  <li>Each search costs 1 credit, deducted from your balance</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
